"use client"

import { useState, useEffect, useCallback } from 'react'
import { Button } from '../components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { Input } from '../components/ui/input'
import { Checkbox } from '../components/ui/checkbox'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select'
import { Plus, Edit, Trash, Tag, PieChart, Image as ImageIcon, Merge } from 'lucide-react'
import API_CONFIG from '../config'
import apiClient from '../lib/api-client'
import { useToast } from '../hooks/use-toast'
import { EditSponsorDialog } from './EditSponsorDialog'

export default function SponsorsTab({ setActiveTab, setSelectedSponsorId }) {
  const [sponsors, setSponsors] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [editingSponsor, setEditingSponsor] = useState(null)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [metrics, setMetrics] = useState({
    totalSponsors: 0,
    totalAppearances: 0,
    averageVisibility: '0.0',
    totalExposureTime: 0
  })
  const [selectedSponsors, setSelectedSponsors] = useState([])
  const [filterCategory] = useState('all')
  const [filterActive] = useState('all')
  const [sortField] = useState('name')
  const [sortDirection] = useState('asc')
  const [isMergeDialogOpen, setIsMergeDialogOpen] = useState(false)
  const [mergeTargetName, setMergeTargetName] = useState('')
  const [mergeTargetCategory, setMergeTargetCategory] = useState('other')
  const [isMerging, setIsMerging] = useState(false)
  const { toast } = useToast()
  
  const fetchSponsors = useCallback(async () => {
    setLoading(true);
    try {
      const apiUrl = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.SPONSORS}`;
      const response = await apiClient.get(apiUrl);
      
      // Handle different response formats
      let sponsorsData = [];
      if (Array.isArray(response.data)) {
        sponsorsData = response.data;
      } else if (response.data && response.data.success && Array.isArray(response.data.sponsors)) {
        sponsorsData = response.data.sponsors;
      } else if (response.data && response.data.success && Array.isArray(response.data.data)) {
        sponsorsData = response.data.data;
      } else if (response.data && typeof response.data === 'object' && !Array.isArray(response.data)) {
        if (Object.keys(response.data).length === 0) {
          sponsorsData = [];
        } else {
          sponsorsData = [response.data];
        }
      } else {
        // Try to handle other formats
        if (response.data === null || response.data === undefined) {
          sponsorsData = [];
        } else if (typeof response.data === 'string') {
          try {
            const parsed = JSON.parse(response.data);
            if (Array.isArray(parsed)) {
              sponsorsData = parsed;
            } else if (parsed && parsed.sponsors && Array.isArray(parsed.sponsors)) {
              sponsorsData = parsed.sponsors;
            } else {
              sponsorsData = [];
            }
          } catch (e) {
            console.error('Failed to parse response data as JSON:', e);
            sponsorsData = [];
          }
        } else {
          sponsorsData = [];
        }
      }
      
      // If we still have no sponsors, fetch from localStorage as fallback
      if (sponsorsData.length === 0) {
        const cachedSponsors = localStorage.getItem('sponsors');
        if (cachedSponsors) {
          try {
            sponsorsData = JSON.parse(cachedSponsors);
          } catch (e) {
            console.error('Error parsing cached sponsors:', e);
            sponsorsData = [];
          }
        } else {
          sponsorsData = [];
        }
      }
      
      // Normalize sponsor data to ensure it has all required properties
      const normalizedSponsors = sponsorsData.map((sponsor, index) => {
        // Extract properties from DynamoDB format if needed
        let rawSponsor = {};
        
        // Handle DynamoDB format (with attribute types like S, N, etc.)
        if (sponsor.id && sponsor.id.S) {
          Object.keys(sponsor).forEach(key => {
            if (sponsor[key].S !== undefined) rawSponsor[key] = sponsor[key].S;
            else if (sponsor[key].N !== undefined) rawSponsor[key] = parseFloat(sponsor[key].N);
            else if (sponsor[key].BOOL !== undefined) rawSponsor[key] = sponsor[key].BOOL;
            else if (sponsor[key].L !== undefined) rawSponsor[key] = sponsor[key].L.map(item => item.S || item.N || item);
            else rawSponsor[key] = sponsor[key];
          });
        } else {
          // Already in regular JSON format
          rawSponsor = { ...sponsor };
        }
        
        // Calculate metrics if they don't exist
        const appearances = rawSponsor.appearances || rawSponsor.total_appearances || rawSponsor.count || 0;
        const visibility = rawSponsor.visibility || rawSponsor.avg_visibility || rawSponsor.visibility_score || 0;
        const exposureTime = rawSponsor.exposureTime || rawSponsor.exposure_time || 0;
        
        return {
          id: rawSponsor.id || rawSponsor.sponsorId || `sponsor-${index}`,
          name: rawSponsor.name || 'Unknown Sponsor',
          category: rawSponsor.category || 'other',
          active: rawSponsor.active !== undefined ? rawSponsor.active : true,
          appearances: typeof appearances === 'number' ? appearances : parseInt(appearances) || 0,
          visibility: typeof visibility === 'number' ? visibility : parseFloat(visibility) || 0,
          exposureTime: typeof exposureTime === 'number' ? exposureTime : parseInt(exposureTime) || 0,
          logoUrl: rawSponsor.logoUrl || rawSponsor.logo_url || '',
          details: rawSponsor.details || '',
          createdAt: rawSponsor.createdAt || rawSponsor.created_at || new Date().toISOString()
        };
      });
      
      setSponsors(normalizedSponsors);

      
      // Save to localStorage for offline fallback
      localStorage.setItem('sponsors', JSON.stringify(normalizedSponsors));
      
      // Calculate totals for metrics
      const totalAppearances = normalizedSponsors.reduce((sum, sponsor) => sum + sponsor.appearances, 0);
      const avgVisibility = normalizedSponsors.length > 0 
        ? normalizedSponsors.reduce((sum, sponsor) => sum + sponsor.visibility, 0) / normalizedSponsors.length 
        : 0;
      const totalExposure = normalizedSponsors.reduce((sum, sponsor) => sum + sponsor.exposureTime, 0);
      
      const calculatedMetrics = {
        totalSponsors: normalizedSponsors.length,
        totalAppearances,
        averageVisibility: avgVisibility.toFixed(1),
        totalExposureTime: totalExposure
      };
      
      setMetrics(calculatedMetrics);
    } catch (error) {
      console.error('Error fetching sponsors:', error);
      // Fallback to local storage
      const cachedSponsors = localStorage.getItem('sponsors');
      if (cachedSponsors) {
        try {
          setSponsors(JSON.parse(cachedSponsors));
        } catch (e) {
          console.error('Error parsing cached sponsors:', e);
          setSponsors([]);
        }
      } else {
        setSponsors([]);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSponsors();
  }, [fetchSponsors]);


  
  const addSponsor = async (sponsorData) => {
    try {
      const response = await apiClient.post(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.SPONSORS}`, {
        ...sponsorData,
        tenantId: API_CONFIG.DEFAULT_TENANT_ID
      });
      
      // Axios doesn't have an 'ok' property, check status code instead
      if (response.status !== 200 && response.status !== 201) {
        throw new Error('Failed to add sponsor');
      }
      
      toast({
        title: 'Success',
        description: 'Sponsor added successfully',
      });
      
      fetchSponsors(); // Refresh the list
    } catch (error) {
      console.error('Error adding sponsor:', error);
      toast({
        title: 'Error',
        description: 'Failed to add sponsor. Please try again.',
        variant: 'destructive'
      });
    }
  };
  
  const updateSponsor = async (id, sponsorData) => {
    try {
      const encodedId = encodeURIComponent(id);
      const updateUrl = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.SPONSORS}/${encodedId}`;
      
      const response = await apiClient.put(updateUrl, {
        ...sponsorData,
        id: id, // Ensure ID is included in the request body
        tenantId: API_CONFIG.DEFAULT_TENANT_ID
      });
      
      // Axios doesn't have an 'ok' property, check status code instead
      if (response.status !== 200 && response.status !== 204) {
        throw new Error(`Failed to update sponsor. Status: ${response.status}`);
      }
      
      toast({
        title: 'Success',
        description: 'Sponsor updated successfully',
      });
      
      fetchSponsors(); // Refresh the list
    } catch (error) {
      console.error('Error updating sponsor:', error);
      toast({
        title: 'Error',
        description: 'Failed to update sponsor. Please try again.',
        variant: 'destructive'
      });
    }
  };
  
  const deleteSponsor = async (id) => {
    try {
      const encodedId = encodeURIComponent(id);
      const deleteUrl = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.SPONSORS}/${encodedId}`;
      const response = await apiClient.delete(deleteUrl);
      
      console.log('Delete response:', response);
      
      // Check if the response indicates success (status 200/204 or success flag)
      // Note: The API client interceptor processes the response, so check response.success
      if (response.success || response.status === 200 || response.status === 204) {
        toast({
          title: 'Success',
          description: 'Sponsor deleted successfully',
        });
        fetchSponsors(); // Refresh the list
      } else {
        throw new Error(response.message || response.data?.message || 'Failed to delete sponsor');
      }
    } catch (error) {
      console.error('Error deleting sponsor:', error);
      console.error('Response data:', error.response?.data);
      console.error('Response status:', error.response?.status);
      
      // Handle processed API error response
      const errorMessage = error.message || 
                          error.response?.data?.message || 
                          error.response?.data?.error || 
                          'Failed to delete sponsor';
      
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive'
      });
    }
  };
  
  const handleEditSponsor = (sponsor) => {
    setEditingSponsor(sponsor);
    setIsEditDialogOpen(true);
  };
  
  const handleSaveSponsor = async (updatedData) => {
    try {
      await updateSponsor(editingSponsor.id, updatedData);
      setIsEditDialogOpen(false);
      setEditingSponsor(null);
    } catch (error) {
      console.error('Error saving sponsor:', error);
      toast({
        title: 'Error',
        description: 'Failed to save sponsor changes',
        variant: 'destructive'
      });
    }
  };
  
  const handleDeleteSponsor = (sponsor) => {
    if (window.confirm(`Are you sure you want to delete ${sponsor.name}?`)) {
      deleteSponsor(sponsor.id);
    }
  };
  
  // Filter and sort sponsors
  const filteredSponsors = sponsors.filter(sponsor => {
    // Text search filter
    const matchesSearch = searchTerm === '' || 
      (sponsor.name && sponsor.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (sponsor.category && sponsor.category.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (sponsor.details && sponsor.details.toLowerCase().includes(searchTerm.toLowerCase()));
    
    // Category filter
    const matchesCategory = filterCategory === 'all' || sponsor.category === filterCategory;
    
    // Active status filter
    const matchesActive = filterActive === 'all' || 
      (filterActive === 'active' && sponsor.active === true) ||
      (filterActive === 'inactive' && sponsor.active === false);
    
    return matchesSearch && matchesCategory && matchesActive;
  }).sort((a, b) => {
    // Handle sorting
    let comparison = 0;
    
    // Sort by the selected field
    if (sortField === 'name') {
      comparison = (a.name || '').localeCompare(b.name || '');
    } else if (sortField === 'category') {
      comparison = (a.category || '').localeCompare(b.category || '');
    } else if (sortField === 'appearances') {
      comparison = (a.appearances || 0) - (b.appearances || 0);
    } else if (sortField === 'exposureTime') {
      comparison = (a.exposureTime || 0) - (b.exposureTime || 0);
    }
    
    // Apply sort direction
    return sortDirection === 'asc' ? comparison : -comparison;
  });


  // Handle sponsor selection for batch operations
  const toggleSponsorSelection = (id) => {
    setSelectedSponsors(prev => {
      let newSelection;
      if (prev.includes(id)) {
        newSelection = prev.filter(sponsorId => sponsorId !== id);
      } else {
        newSelection = [...prev, id];
      }
      console.log('Sponsor selection changed:', { id, prev, newSelection });
      return newSelection;
    });
  };

  // Select/deselect all sponsors
  const toggleSelectAll = () => {
    let newSelection;
    if (selectedSponsors.length === filteredSponsors.length) {
      newSelection = [];
    } else {
      newSelection = filteredSponsors.map(sponsor => sponsor.id);
    }
    console.log('Toggle select all:', { 
      current: selectedSponsors.length, 
      total: filteredSponsors.length, 
      newSelection 
    });
    setSelectedSponsors(newSelection);
  };

  // Handle merge sponsors
  const handleMergeSponsors = () => {
    if (selectedSponsors.length < 2) {
      toast({
        title: "Selection Required",
        description: "Please select at least 2 sponsors to merge.",
        variant: "destructive"
      });
      return;
    }

    // Set default merge target name to the first selected sponsor
    const firstSelectedSponsor = sponsors.find(s => s.id === selectedSponsors[0]);
    if (firstSelectedSponsor) {
      setMergeTargetName(firstSelectedSponsor.name);
      setMergeTargetCategory(firstSelectedSponsor.category);
    }
    
    setIsMergeDialogOpen(true);
  };

  // Handle delete selected sponsors
  const handleDeleteSelected = async () => {
    if (selectedSponsors.length === 0) {
      toast({
        title: "Selection Required",
        description: "Please select at least 1 sponsor to delete.",
        variant: "destructive"
      });
      return;
    }

    const confirmMessage = `Are you sure you want to delete ${selectedSponsors.length} sponsor${selectedSponsors.length === 1 ? '' : 's'}? This action cannot be undone.`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      // Delete each selected sponsor
      const deletePromises = selectedSponsors.map(sponsorId => 
        apiClient.delete(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.SPONSORS}/${sponsorId}`)
      );

      await Promise.all(deletePromises);

      toast({
        title: "Sponsors Deleted",
        description: `Successfully deleted ${selectedSponsors.length} sponsor${selectedSponsors.length === 1 ? '' : 's'}.`,
        duration: 3000
      });

      // Clear selections and refresh data
      setSelectedSponsors([]);
      fetchSponsors();
    } catch (error) {
      console.error('Error deleting sponsors:', error);
      toast({
        title: "Delete Failed",
        description: "Failed to delete some sponsors. Please try again.",
        variant: "destructive",
        duration: 5000
      });
    }
  };

  // Execute sponsor merge
  const executeMerge = async () => {
    if (!mergeTargetName.trim()) {
      toast({
        title: "Name Required",
        description: "Please enter a name for the merged sponsor.",
        variant: "destructive"
      });
      return;
    }

    setIsMerging(true);
    
    try {
      const mergeData = {
        sponsorIds: selectedSponsors,
        targetName: mergeTargetName.trim(),
        targetCategory: mergeTargetCategory
      };

      const apiUrl = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.SPONSORS}/merge`;
      const response = await apiClient.post(apiUrl, mergeData);

      if (response.data && response.data.success) {
        toast({
          title: "Sponsors Merged",
          description: `Successfully merged ${selectedSponsors.length} sponsors into "${mergeTargetName}".`
        });
        
        // Refresh sponsors list
        await fetchSponsors();
        
        // Clear selection and close dialog
        setSelectedSponsors([]);
        setIsMergeDialogOpen(false);
        setMergeTargetName('');
        setMergeTargetCategory('other');
      } else {
        throw new Error(response.data?.message || 'Merge failed');
      }
    } catch (error) {
      console.error('Error merging sponsors:', error);
      toast({
        title: "Merge Failed",
        description: error.response?.data?.message || error.message || "Failed to merge sponsors. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsMerging(false);
    }
  };

  const categoryColors = {
    kit: 'bg-blue-100 text-blue-800',
    led_board: 'bg-green-100 text-green-800',
    pitchside: 'bg-yellow-100 text-yellow-800',
    other: 'bg-purple-100 text-purple-800'
  };
  
  const formatCategory = (category) => {
    return category.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };


  // Handle logo management
  const viewSponsorPhotos = (sponsor) => {
    try {
      // Set the selected sponsor ID in the parent component
      if (setSelectedSponsorId) {
        setSelectedSponsorId(sponsor.id);
      }
      
      // Navigate to the library tab
      if (setActiveTab) {
        setActiveTab('library');
      }
    } catch (error) {
      console.error('Error navigating to library tab:', error);
      // Fallback to showing the alert
      alert(`Viewing photos for ${sponsor.name}. Navigate to the Library tab to see images containing this sponsor.`);
    }
  };


  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Sponsor Management</h2>
        <div className="flex gap-2">
          {/* Debug info - remove after fixing */}
          <div className="text-sm text-gray-600 px-2 py-1 bg-yellow-100 rounded">
            Selected: {selectedSponsors.length} | Sponsors: {filteredSponsors.length}
          </div>
          
          {selectedSponsors.length >= 2 && (
            <Button variant="outline" onClick={handleMergeSponsors}>
              <Merge className="mr-2 h-4 w-4" /> Merge Selected ({selectedSponsors.length})
            </Button>
          )}
          {selectedSponsors.length >= 1 && (
            <Button variant="destructive" onClick={handleDeleteSelected}>
              <Trash className="mr-2 h-4 w-4" /> Delete Selected ({selectedSponsors.length})
            </Button>
          )}
          
          <Button onClick={() => {
            // Simple example - in a real app, you'd use a modal form
            const name = prompt('Enter sponsor name:');
            if (name) {
              addSponsor({ name, category: 'other' });
            }
          }}>
            <Plus className="mr-2 h-4 w-4" /> Add New Sponsor
          </Button>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center">
              <Tag className="mr-2 h-5 w-5" /> Total Sponsors
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold">{loading ? '...' : metrics.totalSponsors}</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center">
              <PieChart className="mr-2 h-5 w-5" /> Total Appearances
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold">{loading ? '...' : metrics.totalAppearances}</p>
          </CardContent>
        </Card>
      </div>
      
      <div className="relative">
        <Input
          placeholder="Search sponsors..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="max-w-md"
        />
      </div>
      
      <div className="overflow-x-auto">
        {loading ? (
          <div className="text-center py-8">Loading sponsors...</div>
        ) : (
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b">
                <th className="text-left py-3 px-4 w-12">
                  <Checkbox
                    checked={selectedSponsors.length > 0 && selectedSponsors.length === filteredSponsors.length}
                    onCheckedChange={toggleSelectAll}
                  />
                </th>
                <th className="text-left py-3 px-4">Name</th>
                <th className="text-left py-3 px-4">Category</th>
                <th className="text-right py-3 px-4">Appearances</th>
                <th className="text-right py-3 px-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredSponsors.length > 0 ? (
                filteredSponsors.map(sponsor => (
                  <tr key={sponsor.id} className="border-b hover:bg-gray-50">
                    <td className="py-3 px-4">
                      <Checkbox
                        checked={selectedSponsors.includes(sponsor.id)}
                        onCheckedChange={() => toggleSponsorSelection(sponsor.id)}
                      />
                    </td>
                    <td className="py-3 px-4 font-medium">{sponsor.name}</td>
                    <td className="py-3 px-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${categoryColors[sponsor.category] || categoryColors.other}`}>
                        {formatCategory(sponsor.category || 'other')}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right">{sponsor.appearances}</td>
                    <td className="py-3 px-4 text-right">
                      <div className="flex justify-end space-x-2">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleEditSponsor(sponsor)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleDeleteSponsor(sponsor)}
                        >
                          <Trash className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => viewSponsorPhotos(sponsor)}
                          title="View photos containing this sponsor"
                        >
                          <ImageIcon className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="py-6 text-center text-gray-500">
                    {searchTerm ? 'No sponsors match your search' : 'No sponsors found'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
      
      {/* Edit Sponsor Dialog */}
      <EditSponsorDialog
        sponsor={editingSponsor}
        isOpen={isEditDialogOpen}
        onClose={() => {
          setIsEditDialogOpen(false);
          setEditingSponsor(null);
        }}
        onSave={handleSaveSponsor}
      />

      {/* Merge Sponsors Dialog */}
      <Dialog open={isMergeDialogOpen} onOpenChange={setIsMergeDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Merge Sponsors</DialogTitle>
            <DialogDescription>
              Merge {selectedSponsors.length} selected sponsors into a single sponsor. All image associations will be transferred to the merged sponsor.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <label htmlFor="merge-name" className="block text-sm font-medium mb-2">
                Merged Sponsor Name
              </label>
              <Input
                id="merge-name"
                placeholder="Enter the name for the merged sponsor"
                value={mergeTargetName}
                onChange={(e) => setMergeTargetName(e.target.value)}
              />
            </div>
            
            <div>
              <label htmlFor="merge-category" className="block text-sm font-medium mb-2">
                Category
              </label>
              <Select value={mergeTargetCategory} onValueChange={setMergeTargetCategory}>
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="kit">Kit</SelectItem>
                  <SelectItem value="main_sponsor">Main Sponsor</SelectItem>
                  <SelectItem value="led_board">LED Board</SelectItem>
                  <SelectItem value="pitchside">Pitchside</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="text-sm text-gray-600">
              <p className="font-medium mb-1">Selected sponsors to merge:</p>
              <ul className="list-disc list-inside space-y-1">
                {selectedSponsors.map(id => {
                  const sponsor = sponsors.find(s => s.id === id);
                  return sponsor ? (
                    <li key={id}>{sponsor.name} ({sponsor.appearances} appearances)</li>
                  ) : null;
                })}
              </ul>
            </div>
          </div>
          
          <DialogFooter className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setIsMergeDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={executeMerge} disabled={isMerging || !mergeTargetName.trim()}>
              {isMerging ? 'Merging...' : 'Merge Sponsors'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
    </div>
  );
}
